import { Vector3, Curve3, Quaternion } from '@babylonjs/core';

/**
 * Spline - A utility class for working with 3D splines
 * 
 * Provides:
 * - Arc-length parameterization for uniform sampling
 * - Closest point queries
 * - Tangent/normal/binormal frame calculations
 * - Position and rotation queries
 */

export interface SplineFrame {
  point: Vector3;
  tangent: Vector3;
  normal: Vector3;
  binormal: Vector3;
  rotation: Quaternion;
}

export class Spline {
  private cumulativeLengths: number[] = [];
  private totalLength: number = 0;

  constructor(private curve: Curve3) {
    this.recalculateLengths();
  }

  private recalculateLengths() {
    const points = this.curve.getPoints();
    this.cumulativeLengths = [0];
    this.totalLength = 0;
    
    for (let i = 0; i < points.length - 1; i++) {
      const dist = Vector3.Distance(points[i], points[i+1]);
      this.totalLength += dist;
      this.cumulativeLengths.push(this.totalLength);
    }
  }

  /**
   * Get the total length of the spline
   */
  public getTotalLength(): number {
    return this.totalLength;
  }

  /**
   * Get point on spline at normalized t (0-1) using arc length
   */
  public getPointAt(t: number): Vector3 {
    const points = this.curve.getPoints();
    const targetDist = t * this.totalLength;
    
    let low = 0;
    let high = this.cumulativeLengths.length - 1;
    let index = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (this.cumulativeLengths[mid] < targetDist) {
        index = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    
    index = Math.max(0, Math.min(points.length - 2, index));
    
    const lenA = this.cumulativeLengths[index];
    const lenB = this.cumulativeLengths[index + 1];
    const segmentLen = lenB - lenA;
    
    const factor = segmentLen > 0.0001 ? (targetDist - lenA) / segmentLen : 0;
    
    return Vector3.Lerp(points[index], points[index + 1], factor);
  }

  /**
   * Get tangent (forward) direction at t (arc-length normalized)
   */
  public getTangentAt(t: number): Vector3 {
    const points = this.curve.getPoints();
    const targetDist = t * this.totalLength;
    
    let low = 0;
    let high = this.cumulativeLengths.length - 1;
    let index = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (this.cumulativeLengths[mid] < targetDist) {
        index = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    
    index = Math.max(0, Math.min(points.length - 2, index));
    
    const p1 = points[index];
    const p2 = points[index + 1];
    return p2.subtract(p1).normalize();
  }

  /**
   * Get normal (up) direction at t
   * For floor-based splines, this is perpendicular to tangent, favoring world up
   */
  public getNormalAt(t: number): Vector3 {
    const tangent = this.getTangentAt(t);
    const worldUp = Vector3.Up();
    
    // If tangent is nearly vertical, use world forward
    if (Math.abs(Vector3.Dot(tangent, worldUp)) > 0.99) {
      const worldForward = new Vector3(0, 0, 1);
      return Vector3.Cross(tangent, worldForward).normalize();
    }
    
    // Normal is perpendicular to tangent, points "up"
    const right = Vector3.Cross(worldUp, tangent).normalize();
    return Vector3.Cross(tangent, right).normalize();
  }

  /**
   * Get binormal (right) direction at t
   */
  public getBinormalAt(t: number): Vector3 {
    const tangent = this.getTangentAt(t);
    const normal = this.getNormalAt(t);
    return Vector3.Cross(tangent, normal).normalize();
  }

  /**
   * Get full coordinate frame at t
   */
  public getFrameAt(t: number): SplineFrame {
    const point = this.getPointAt(t);
    const tangent = this.getTangentAt(t);
    const normal = this.getNormalAt(t);
    const binormal = Vector3.Cross(tangent, normal).normalize();
    const rotation = Quaternion.RotationQuaternionFromAxis(binormal, normal, tangent);

    return {
      point,
      tangent,
      normal,
      binormal,
      rotation
    };
  }

  /**
   * Find closest point on spline using XZ distance (ignore Y for 2.5D)
   * Returns point and arc-length normalized t (0-1)
   * @param position - Position to find closest point to
   * @param lastClosestIndex - Hint for optimization (last known closest index)
   * @param searchRadius - Radius to search around lastClosestIndex (default: 150)
   */
  public findClosestPoint(
    position: Vector3, 
    lastClosestIndex: number = 0,
    searchRadius: number = 150
  ): { point: Vector3; t: number; closestIndex: number } {
    const points = this.curve.getPoints();
    let closestDistance = Infinity;
    let closestIndex = lastClosestIndex;

    const start = Math.max(0, lastClosestIndex - searchRadius);
    const end = Math.min(points.length, lastClosestIndex + searchRadius);

    // 1. Find closest vertex
    for (let i = start; i < end; i++) {
      const dx = points[i].x - position.x;
      const dz = points[i].z - position.z;
      const distanceSq = dx * dx + dz * dz;
      
      if (distanceSq < closestDistance) {
        closestDistance = distanceSq;
        closestIndex = i;
      }
    }

    // 2. Interpolate between neighbors to find true closest point on segment
    let bestPoint = points[closestIndex];
    let bestDistSq = closestDistance;
    let bestT = this.cumulativeLengths[closestIndex] / this.totalLength;

    // Helper to project point onto segment AB
    const projectOnSegment = (p: Vector3, a: Vector3, b: Vector3): { point: Vector3, factor: number, distSq: number } => {
      const ap = p.subtract(a);
      const ab = b.subtract(a);
      // Project on XZ plane only
      const ap_xz = new Vector3(ap.x, 0, ap.z);
      const ab_xz = new Vector3(ab.x, 0, ab.z);
      
      const lenSq = ab_xz.lengthSquared();
      if (lenSq < 0.0001) return { point: a, factor: 0, distSq: ap_xz.lengthSquared() };
      
      let t = Vector3.Dot(ap_xz, ab_xz) / lenSq;
      t = Math.max(0, Math.min(1, t));
      
      const projected = a.add(ab.scale(t));
      const dx = projected.x - p.x;
      const dz = projected.z - p.z;
      return { point: projected, factor: t, distSq: dx*dx + dz*dz };
    };

    // Check previous segment
    if (closestIndex > 0) {
      const prev = closestIndex - 1;
      const res = projectOnSegment(position, points[prev], points[closestIndex]);
      if (res.distSq < bestDistSq) {
        bestDistSq = res.distSq;
        bestPoint = res.point;
        // Interpolate arc length
        const lenA = this.cumulativeLengths[prev];
        const lenB = this.cumulativeLengths[closestIndex];
        const currentLen = lenA + (lenB - lenA) * res.factor;
        bestT = currentLen / this.totalLength;
      }
    }

    // Check next segment
    if (closestIndex < points.length - 1) {
      const next = closestIndex + 1;
      const res = projectOnSegment(position, points[closestIndex], points[next]);
      if (res.distSq < bestDistSq) {
        bestDistSq = res.distSq;
        bestPoint = res.point;
        // Interpolate arc length
        const lenA = this.cumulativeLengths[closestIndex];
        const lenB = this.cumulativeLengths[next];
        const currentLen = lenA + (lenB - lenA) * res.factor;
        bestT = currentLen / this.totalLength;
      }
    }

    return { point: bestPoint, t: bestT, closestIndex };
  }

  /**
   * Get the underlying Curve3
   */
  public getCurve(): Curve3 {
    return this.curve;
  }

  /**
   * Get the number of points in the spline
   */
  public getPointCount(): number {
    return this.curve.getPoints().length;
  }

  /**
   * Set a new curve and recalculate lengths
   */
  public setCurve(curve: Curve3): void {
    this.curve = curve;
    this.recalculateLengths();
  }
}
